import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    FolderOpen,
    Upload,
    Loader2,
    Pin,
    PinOff,
    Edit3,
    Trash2,
    X,
    CheckCircle2,
    AlertTriangle,
    Wand2,
    BookOpenCheck,
    Download,
    Send,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../../lib/api";
import { useConfirm } from "../../ConfirmDialog";
import { MemoView, fileMeta, formatBytes, formatResourceDate } from "../../ResourcesSection";

/**
 * Iter-31 — Onglet "Ressources" du backoffice (asso ET admin Shariti).
 *
 * L'asso dépose un fichier (PDF, PPTX, DOCX…) → il devient téléchargeable par
 * les ambassadeurs ET une fiche de lecture structurée est préparée
 * automatiquement (générée par IA en coulisses).
 *
 * RÈGLE PRODUIT : côté association, AUCUNE mention "IA" ne doit apparaître
 * (wording neutre : "fiche ambassadeur"). Côté admin Shariti (adminMode),
 * le wording IA est autorisé.
 */
const EXTRACTABLE_EXTS = ["pdf", "pptx", "docx", "txt", "md", "csv"];
const ACCEPT = ".pdf,.ppt,.pptx,.doc,.docx,.txt,.md,.csv,.xls,.xlsx,.png,.jpg,.jpeg,.webp";
const MAX_MB = 50;

function initialComposer() {
    return { _editingId: null, title: "", description: "" };
}

export default function AssoResourcesTab({ onChanged, adminMode = false, assoId = null }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [composer, setComposer] = useState(initialComposer());
    const [file, setFile] = useState(null); // {storage_path, original_filename, content_type, size}
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [saving, setSaving] = useState(false);
    const [memoOpen, setMemoOpen] = useState(null); // resource being viewed
    const inputRef = useRef(null);
    const composerRef = useRef(null);
    const confirm = useConfirm();

    const listUrl = adminMode ? `/admin/associations/${assoId}/resources` : "/asso/resources";
    const itemUrl = (rid) => (adminMode ? `/admin/resources/${rid}` : `/asso/resources/${rid}`);
    const ficheLabel = adminMode ? "fiche IA" : "fiche ambassadeur";

    const reload = async () => {
        try {
            const r = await api.get(listUrl);
            setItems(r.data || []);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        reload();
    }, []);

    // Poll tant qu'une fiche est en préparation
    useEffect(() => {
        if (!items.some((r) => r.memo_status === "generating")) return undefined;
        const t = setTimeout(reload, 4000);
        return () => clearTimeout(t);
    }, [items]);

    const handleFilePick = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > MAX_MB * 1024 * 1024) {
            toast.error(`Fichier trop volumineux (max ${MAX_MB} Mo)`);
            return;
        }
        setUploading(true);
        setProgress(0);
        try {
            const fd = new FormData();
            fd.append("file", f);
            fd.append("kind", "resource");
            const { data } = await api.post("/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                onUploadProgress: (p) => {
                    if (p.total) setProgress(Math.round((p.loaded / p.total) * 100));
                },
            });
            setFile({
                storage_path: data.storage_path,
                original_filename: data.original_filename,
                content_type: data.content_type,
                size: data.size,
            });
            if (!composer.title.trim()) {
                // Pré-remplit le titre avec le nom du fichier (sans extension)
                const base = (data.original_filename || "").replace(/\.[^.]+$/, "");
                if (base) setComposer((c) => ({ ...c, title: base }));
            }
            toast.success("Fichier prêt à publier");
        } catch {
            toast.error("Erreur d'upload");
        } finally {
            setUploading(false);
            setProgress(0);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    const startEdit = (item) => {
        setComposer({
            _editingId: item.resource_id,
            title: item.title || "",
            description: item.description || "",
        });
        setFile(null);
        composerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const cancelEdit = () => {
        setComposer(initialComposer());
        setFile(null);
    };

    const submit = async () => {
        if (!composer.title.trim()) {
            toast.error("Donne un titre à ta ressource");
            return;
        }
        if (!composer._editingId && !file) {
            toast.error("Ajoute un fichier (PDF, PowerPoint, Word…)");
            return;
        }
        setSaving(true);
        try {
            if (composer._editingId) {
                await api.patch(itemUrl(composer._editingId), {
                    title: composer.title,
                    description: composer.description || "",
                });
                toast.success("Ressource mise à jour");
            } else {
                const ext = (file.original_filename || "").split(".").pop()?.toLowerCase();
                await api.post(listUrl, {
                    title: composer.title,
                    description: composer.description || null,
                    file_url: `/api/files/${file.storage_path}`,
                    file_name: file.original_filename,
                    content_type: file.content_type,
                    file_size: file.size,
                });
                if (EXTRACTABLE_EXTS.includes(ext)) {
                    toast.success(
                        adminMode
                            ? "Ressource publiée — génération de la fiche IA lancée"
                            : "Ressource publiée — la fiche ambassadeur se prépare ✨",
                    );
                } else {
                    toast.success("Ressource publiée");
                }
            }
            cancelEdit();
            await reload();
            onChanged?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erreur");
        } finally {
            setSaving(false);
        }
    };

    const togglePin = async (item) => {
        try {
            await api.patch(itemUrl(item.resource_id), { pinned: !item.pinned });
            await reload();
        } catch {
            toast.error("Erreur");
        }
    };

    const remove = async (item) => {
        const ok = await confirm({
            title: "Supprimer cette ressource ?",
            description: `« ${item.title} » ne sera plus visible par les ambassadeurs. Cette action est irréversible.`,
            confirmLabel: "Supprimer",
            cancelLabel: "Annuler",
            tone: "danger",
        });
        if (!ok) return;
        try {
            await api.delete(itemUrl(item.resource_id));
            toast.success("Ressource supprimée");
            if (composer._editingId === item.resource_id) cancelEdit();
            await reload();
            onChanged?.();
        } catch {
            toast.error("Erreur");
        }
    };

    const regenerateMemo = async (item) => {
        try {
            await api.post(`${itemUrl(item.resource_id)}/memo`);
            toast.success(
                adminMode ? "Génération IA relancée" : "Préparation de la fiche relancée",
            );
            await reload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erreur");
        }
    };

    const isEditing = !!composer._editingId;

    return (
        <div className="space-y-6" data-testid="asso-resources-tab">
            {/* COMPOSER */}
            <motion.div
                ref={composerRef}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid="resource-composer"
                className={`relative rounded-3xl border p-5 sm:p-6 transition-all ${
                    isEditing
                        ? "bg-shariti-orange/8 border-shariti-orange/40 ring-2 ring-shariti-orange/30"
                        : "bg-white/3 border-white/10"
                }`}
            >
                <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 rounded-2xl bg-shariti-orange/15 border border-shariti-orange/35 flex items-center justify-center shrink-0">
                        <FolderOpen className="w-5 h-5 text-shariti-orange-light" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-thin tracking-[0.25em] uppercase text-shariti-orange-light">
                            {isEditing ? "Modifier la ressource" : "Nouvelle ressource"}
                        </p>
                        <h3 className="text-base font-extrabold text-white leading-tight">
                            {isEditing
                                ? "Mets à jour le titre ou la description"
                                : "Partage un document avec tes ambassadeurs"}
                        </h3>
                        {!isEditing && (
                            <p className="text-xs text-white/50 font-light mt-1 leading-relaxed">
                                PowerPoint de formation, PDF, document Word… Le fichier devient
                                téléchargeable et il est automatiquement transformé en{" "}
                                <span className="text-shariti-orange-light font-bold">
                                    fiche de lecture claire et designée
                                </span>{" "}
                                pour les ambassadeurs.
                            </p>
                        )}
                    </div>
                    {isEditing && (
                        <button
                            type="button"
                            onClick={cancelEdit}
                            data-testid="resource-cancel-edit"
                            className="p-2 rounded-xl hover:bg-white/5 text-white/55"
                            aria-label="Annuler"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    )}
                </div>

                <div className="space-y-3">
                    <input
                        type="text"
                        value={composer.title}
                        onChange={(e) => setComposer({ ...composer, title: e.target.value })}
                        placeholder="Titre de la ressource (ex : Support de formation — Objet social)"
                        data-testid="resource-title-input"
                        className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white text-sm outline-none focus:border-shariti-orange/40 placeholder:text-white/30"
                    />
                    <textarea
                        value={composer.description}
                        onChange={(e) => setComposer({ ...composer, description: e.target.value })}
                        placeholder="Description courte (optionnel) — à quoi sert ce document sur le terrain ?"
                        rows={2}
                        data-testid="resource-desc-input"
                        className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white text-sm outline-none focus:border-shariti-orange/40 placeholder:text-white/30 resize-none"
                    />

                    {!isEditing && (
                        <div>
                            <input
                                ref={inputRef}
                                type="file"
                                accept={ACCEPT}
                                onChange={handleFilePick}
                                className="hidden"
                                data-testid="resource-file-input"
                            />
                            {file ? (
                                <div className="flex items-center gap-3 rounded-2xl border border-shariti-orange/35 bg-shariti-orange/8 px-4 py-3">
                                    <CheckCircle2 className="w-4 h-4 text-shariti-orange-light shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-bold text-white truncate">
                                            {file.original_filename}
                                        </p>
                                        <p className="text-[10px] text-white/45">
                                            {formatBytes(file.size) || "—"}
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setFile(null)}
                                        data-testid="resource-file-clear"
                                        className="p-1.5 rounded-lg hover:bg-white/10 text-white/55"
                                        aria-label="Retirer le fichier"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => inputRef.current?.click()}
                                    disabled={uploading}
                                    data-testid="resource-upload-btn"
                                    className="w-full flex items-center justify-center gap-2.5 rounded-2xl border-2 border-dashed border-white/15 hover:border-shariti-orange/40 hover:bg-shariti-orange/5 px-4 py-5 text-sm font-bold text-white/65 hover:text-white transition-all disabled:opacity-60"
                                >
                                    {uploading ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Upload en cours… {progress}%
                                        </>
                                    ) : (
                                        <>
                                            <Upload className="w-4 h-4" />
                                            Choisir un fichier (PDF, PPTX, DOCX… max {MAX_MB} Mo)
                                        </>
                                    )}
                                </button>
                            )}
                        </div>
                    )}

                    <button
                        type="button"
                        onClick={submit}
                        disabled={saving || uploading || !composer.title.trim() || (!isEditing && !file)}
                        data-testid="resource-submit-btn"
                        className="inline-flex items-center gap-2 bg-shariti-orange hover:bg-shariti-orange-light text-white font-extrabold px-5 py-3 rounded-2xl text-sm transition-all disabled:opacity-40"
                    >
                        {saving ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <Send className="w-4 h-4" />
                        )}
                        {isEditing ? "Enregistrer" : "Publier la ressource"}
                    </button>
                </div>
            </motion.div>

            {/* LISTE */}
            {loading ? (
                <div className="flex justify-center py-10">
                    <Loader2 className="w-5 h-5 animate-spin text-shariti-orange" />
                </div>
            ) : items.length === 0 ? (
                <div className="text-center py-12 rounded-3xl border border-white/8 bg-white/2" data-testid="resources-list-empty">
                    <div className="w-14 h-14 rounded-3xl bg-shariti-orange/10 flex items-center justify-center mx-auto mb-3">
                        <FolderOpen className="w-6 h-6 text-shariti-orange/70" />
                    </div>
                    <p className="text-white/55 text-sm font-light max-w-sm mx-auto leading-relaxed">
                        Aucune ressource pour le moment. Dépose un premier document — il sera
                        restitué de façon premium dans l'espace de tes ambassadeurs.
                    </p>
                </div>
            ) : (
                <div className="space-y-3" data-testid="resources-list">
                    {items.map((r, idx) => (
                        <ResourceRow
                            key={r.resource_id}
                            r={r}
                            idx={idx}
                            adminMode={adminMode}
                            onPin={() => togglePin(r)}
                            onEdit={() => startEdit(r)}
                            onDelete={() => remove(r)}
                            onRegenerate={() => regenerateMemo(r)}
                            onViewMemo={() => setMemoOpen(r)}
                        />
                    ))}
                </div>
            )}

            {/* MODAL fiche */}
            <AnimatePresence>
                {memoOpen && (
                    <MemoModal r={memoOpen} onClose={() => setMemoOpen(null)} />
                )}
            </AnimatePresence>
        </div>
    );
}


function ResourceRow({ r, idx, adminMode, onPin, onEdit, onDelete, onRegenerate, onViewMemo }) {
    const meta = fileMeta(r.file_ext);
    const Icon = meta.Icon;
    const size = formatBytes(r.file_size);
    const extractable = EXTRACTABLE_EXTS.includes((r.file_ext || "").toLowerCase());

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, delay: idx * 0.03 }}
            className={`rounded-2xl border p-4 transition-all ${
                r.pinned
                    ? "border-shariti-orange/35 bg-gradient-to-br from-shariti-orange/8 to-transparent"
                    : "border-white/10 bg-white/3"
            }`}
            data-testid={`bo-resource-card-${idx}`}
        >
            <div className="flex items-start gap-3">
                <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border"
                    style={{ background: `${meta.color}1A`, borderColor: `${meta.color}45` }}
                >
                    <Icon className="w-5 h-5" style={{ color: meta.color }} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="text-sm font-extrabold text-white leading-tight">
                            {r.title}
                        </h4>
                        {r.pinned && (
                            <span className="inline-flex items-center gap-1 text-[9px] font-extrabold uppercase tracking-[0.18em] text-shariti-orange-light">
                                <Pin className="w-2.5 h-2.5" />
                                Épinglé
                            </span>
                        )}
                    </div>
                    {r.description && (
                        <p className="text-xs text-white/55 font-light leading-relaxed mt-0.5 line-clamp-2">
                            {r.description}
                        </p>
                    )}
                    <p className="text-[10px] text-white/40 font-light mt-1">
                        <span className="font-extrabold tracking-wider uppercase" style={{ color: meta.color }}>
                            {meta.label}
                        </span>
                        {size && <> · {size}</>}
                        {r.created_at && <> · {formatResourceDate(r.created_at)}</>}
                        {adminMode && r.created_by_role === "admin" && <> · publié par Shariti</>}
                    </p>

                    {/* Statut fiche */}
                    <div className="flex flex-wrap items-center gap-2 mt-2.5">
                        {r.memo_status === "ready" && (
                            <>
                                <span className="inline-flex items-center gap-1 text-[10px] font-extrabold text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 px-2 py-1 rounded-full" data-testid={`memo-status-ready-${idx}`}>
                                    <CheckCircle2 className="w-3 h-3" />
                                    Fiche prête
                                </span>
                                <button
                                    type="button"
                                    onClick={onViewMemo}
                                    data-testid={`memo-view-btn-${idx}`}
                                    className="inline-flex items-center gap-1.5 text-[11px] font-extrabold text-shariti-orange-light bg-shariti-orange/10 border border-shariti-orange/30 hover:bg-shariti-orange/20 px-2.5 py-1 rounded-full transition-all"
                                >
                                    <BookOpenCheck className="w-3 h-3" />
                                    Voir la fiche
                                </button>
                            </>
                        )}
                        {r.memo_status === "generating" && (
                            <span className="inline-flex items-center gap-1.5 text-[10px] font-bold text-white/60 bg-white/5 px-2.5 py-1 rounded-full" data-testid={`memo-status-generating-${idx}`}>
                                <Loader2 className="w-3 h-3 animate-spin" />
                                {adminMode ? "Génération IA en cours…" : "Fiche en préparation…"}
                            </span>
                        )}
                        {r.memo_status === "failed" && (
                            <>
                                <span className="inline-flex items-center gap-1 text-[10px] font-extrabold text-red-300 bg-red-500/10 border border-red-500/30 px-2 py-1 rounded-full" data-testid={`memo-status-failed-${idx}`}>
                                    <AlertTriangle className="w-3 h-3" />
                                    Fiche indisponible
                                </span>
                                <button
                                    type="button"
                                    onClick={onRegenerate}
                                    data-testid={`memo-retry-btn-${idx}`}
                                    className="inline-flex items-center gap-1.5 text-[11px] font-extrabold text-white/75 bg-white/5 border border-white/15 hover:bg-white/10 px-2.5 py-1 rounded-full transition-all"
                                >
                                    <Wand2 className="w-3 h-3" />
                                    {adminMode ? "Relancer l'IA" : "Réessayer"}
                                </button>
                            </>
                        )}
                        {r.memo_status === "none" && extractable && (
                            <button
                                type="button"
                                onClick={onRegenerate}
                                data-testid={`memo-generate-btn-${idx}`}
                                className="inline-flex items-center gap-1.5 text-[11px] font-extrabold text-shariti-orange-light bg-shariti-orange/10 border border-shariti-orange/30 hover:bg-shariti-orange/20 px-2.5 py-1 rounded-full transition-all"
                            >
                                <Wand2 className="w-3 h-3" />
                                {adminMode ? "Générer la fiche IA" : "Créer la fiche ambassadeur"}
                            </button>
                        )}
                        {r.memo_status === "none" && !extractable && (
                            <span className="text-[10px] text-white/35 font-light">
                                Fichier publié tel quel (téléchargeable)
                            </span>
                        )}
                    </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 shrink-0">
                    <a
                        href={r.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`bo-resource-download-${idx}`}
                        title="Télécharger"
                        className="p-2 rounded-xl hover:bg-white/8 text-white/45 hover:text-white transition-all"
                    >
                        <Download className="w-4 h-4" />
                    </a>
                    <button
                        type="button"
                        onClick={onPin}
                        data-testid={`bo-resource-pin-${idx}`}
                        title={r.pinned ? "Désépingler" : "Épingler en haut"}
                        className={`p-2 rounded-xl transition-all ${
                            r.pinned
                                ? "text-shariti-orange-light hover:bg-shariti-orange/10"
                                : "text-white/45 hover:text-white hover:bg-white/8"
                        }`}
                    >
                        {r.pinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
                    </button>
                    <button
                        type="button"
                        onClick={onEdit}
                        data-testid={`bo-resource-edit-${idx}`}
                        title="Modifier"
                        className="p-2 rounded-xl hover:bg-white/8 text-white/45 hover:text-white transition-all"
                    >
                        <Edit3 className="w-4 h-4" />
                    </button>
                    <button
                        type="button"
                        onClick={onDelete}
                        data-testid={`bo-resource-delete-${idx}`}
                        title="Supprimer"
                        className="p-2 rounded-xl hover:bg-red-500/10 text-white/45 hover:text-red-300 transition-all"
                    >
                        <Trash2 className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </motion.div>
    );
}


function MemoModal({ r, onClose }) {
    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[90] bg-black/75 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
            onClick={onClose}
            data-testid="memo-modal"
        >
            <motion.div
                initial={{ scale: 0.96, y: 14, opacity: 0 }}
                animate={{ scale: 1, y: 0, opacity: 1 }}
                exit={{ scale: 0.96, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-shariti-black border border-white/10 rounded-3xl w-full max-w-2xl my-6 p-6 sm:p-8 pb-28 sm:pb-8 shadow-2xl relative"
            >
                <button
                    type="button"
                    onClick={onClose}
                    data-testid="memo-modal-close"
                    className="absolute top-4 right-4 w-9 h-9 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center"
                    aria-label="Fermer"
                >
                    <X className="w-4 h-4 text-white/70" />
                </button>
                <p className="text-[10px] font-thin tracking-[0.3em] uppercase text-shariti-orange-light mb-1">
                    Aperçu — ce que voient tes ambassadeurs
                </p>
                <h3 className="text-xl sm:text-2xl font-extrabold text-white tracking-tight mb-1 pr-10">
                    {r.memo?.headline || r.title}
                </h3>
                <p className="text-[11px] text-white/40 font-light mb-5">
                    Source : {r.file_name || r.title}
                </p>
                <MemoView memo={r.memo} />
                <a
                    href={r.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 mt-6 px-4 py-2.5 rounded-xl text-xs font-extrabold text-white/75 bg-white/5 border border-white/10 hover:bg-white/10 hover:text-white transition-all"
                >
                    <Download className="w-3.5 h-3.5" />
                    Télécharger l'original
                </a>
            </motion.div>
        </motion.div>
    );
}
